import React from 'react';
import { StepProps } from '../types';
import styled, { css } from 'styled-components';
import { mix } from '$ui/Flo/util';
import { Icon } from '$ui/Flo/Icon';
import { FlowNodeType } from '$types';
import { Patient } from '$state/types';
import {
    useEthnicityCodes,
    usePatientTitles,
    usePaymentPlans,
} from '$ui/Actions/Flow/Steps/DentallySteps/hooks';
import { Controller, FieldError, FormProvider, useForm } from 'react-hook-form';
import { useId } from 'react-aria';
import InputMask from 'react-input-mask';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    DentallyPatientAddFormSchema,
    DentallyPatientAddFormType,
} from '$state/concerns/dentally/patients';
import { DentallyPatient } from '$state/types/patients';

interface AddPatientValue {
    dentally?: {
        link?: {
            patientId: string;
            dentallyId: number;
        };
        create?: {
            patientId: string;
            values: DentallyPatient | null;
        };
    };
}

export interface Props extends StepProps<AddPatientValue> {
    type: FlowNodeType.addPatient;
    patient?: Patient;
}

export const AddPatientStep = ({
    onBack,
    onNext,
    proceedable,
    patient,
    onChange,
}: Props) => {
    const titles = usePatientTitles();
    const ethnicityCodes = useEthnicityCodes();
    const paymentPlans = usePaymentPlans();

    const onAddPatient = async (data: DentallyPatientAddFormType) => {
        if (patient) {
            onChange({
                dentally: {
                    create: {
                        patientId: patient.id,
                        values: {
                            ...data,
                            date_of_birth: data.date_of_birth
                                .split('/')
                                .reverse()
                                .join('-'),
                        },
                    },
                },
            });
        }
    };

    const formRef = React.useRef<HTMLFormElement>(null);
    const form = useForm<DentallyPatientAddFormType>({
        mode: 'all',
        reValidateMode: 'onChange',
        resolver: zodResolver(DentallyPatientAddFormSchema),
        defaultValues: {
            title: '',
            first_name: patient?.first_name,
            last_name: patient?.last_name,
            email_address: patient?.email,
            mobile_phone: patient?.phone_number?.number,
            date_of_birth: '',
            gender: 'male',
            ethnicity: '',
            address_line_1: '',
            address_line_2: '',
            postcode: '',
            payment_plan_id: '',
        },
    });

    return (
        <Container data-cy="add-patient-step">
            <Header>
                Add {patient ? patient.first_name : 'patient'} to Dentally
            </Header>
            <Content>
                <FormProvider {...form}>
                    <Form ref={formRef}>
                        <Controller
                            control={form.control}
                            name="title"
                            render={({ field }) => {
                                const id = useId();
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={id}>
                                            Title
                                            <Select
                                                id={id}
                                                data-cy="add-dentally-patient-title"
                                                {...field}
                                            >
                                                <option value="">Select</option>
                                                {titles.isFetched &&
                                                    titles.data &&
                                                    titles.data.map((title) => (
                                                        <option
                                                            key={title}
                                                            value={title}
                                                        >
                                                            {title}
                                                        </option>
                                                    ))}
                                            </Select>
                                        </Label>
                                        <ErrorMessage
                                            error={form.formState.errors.title}
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <NameHolder>
                            <Controller
                                name="first_name"
                                control={form.control}
                                render={({ field }) => {
                                    return (
                                        <InputWrapper>
                                            <Label htmlFor={field.name}>
                                                First name
                                                <Input
                                                    {...field}
                                                    hasError={
                                                        !!form.formState.errors
                                                            .first_name
                                                    }
                                                    data-cy="add-dentally-patient-first-name"
                                                />
                                            </Label>
                                            <ErrorMessage
                                                error={
                                                    form.formState.errors
                                                        .first_name
                                                }
                                            />
                                        </InputWrapper>
                                    );
                                }}
                            />

                            <Controller
                                name="last_name"
                                control={form.control}
                                render={({ field }) => {
                                    return (
                                        <InputWrapper>
                                            <Label htmlFor={field.name}>
                                                Last name
                                                <Input
                                                    {...field}
                                                    hasError={
                                                        !!form.formState.errors
                                                            .last_name
                                                    }
                                                    data-cy="add-dentally-patient-last-name"
                                                />
                                            </Label>
                                            <ErrorMessage
                                                error={
                                                    form.formState.errors
                                                        .last_name
                                                }
                                            />
                                        </InputWrapper>
                                    );
                                }}
                            />
                        </NameHolder>

                        <Controller
                            name="email_address"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Email address
                                            <Input
                                                {...field}
                                                hasError={
                                                    !!form.formState.errors
                                                        .email_address
                                                }
                                                data-cy="add-dentally-patient-email-address"
                                                placeholder={'(Optional)'}
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors
                                                    .email_address
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            name="mobile_phone"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Mobile phone
                                            <Input
                                                {...field}
                                                hasError={
                                                    !!form.formState.errors
                                                        .mobile_phone
                                                }
                                                data-cy="add-dentally-patient-mobile-phone"
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors
                                                    .mobile_phone
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            name="date_of_birth"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Date of birth
                                            <InputWithMask
                                                {...field}
                                                mask="99/99/9999"
                                                maskPlaceholder="DD/MM/YYYY"
                                                alwaysShowMask={true}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                                value={field.value}
                                                data-cy="add-dentally-patient-date-of-birth"
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors
                                                    .date_of_birth
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            name="gender"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Gender
                                            <ToggleHolder>
                                                <ToggleButton
                                                    $isLeft={true}
                                                    $isSelected={
                                                        field.value === 'male'
                                                    }
                                                    onClick={() =>
                                                        form.setValue(
                                                            'gender',
                                                            'male',
                                                        )
                                                    }
                                                >
                                                    Male
                                                </ToggleButton>
                                                <ToggleButton
                                                    $isLeft={false}
                                                    $isSelected={
                                                        field.value === 'female'
                                                    }
                                                    onClick={() =>
                                                        form.setValue(
                                                            'gender',
                                                            'female',
                                                        )
                                                    }
                                                >
                                                    Female
                                                </ToggleButton>
                                            </ToggleHolder>
                                        </Label>
                                        <ErrorMessage
                                            error={form.formState.errors.gender}
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            control={form.control}
                            name="ethnicity"
                            render={({ field }) => {
                                const id = useId();
                                return (
                                    <>
                                        <InputWrapper>
                                            <Label htmlFor={id}>
                                                Ethnicity
                                                <Select
                                                    id={id}
                                                    data-cy="add-dentally-patient-ethnicity"
                                                    {...field}
                                                >
                                                    <option value="">
                                                        Select
                                                    </option>
                                                    {ethnicityCodes.isFetched &&
                                                        ethnicityCodes.data &&
                                                        Object.entries(
                                                            ethnicityCodes.data,
                                                        ).map(
                                                            ([value, key]) => (
                                                                <option
                                                                    key={key}
                                                                    value={key}
                                                                >
                                                                    {value}
                                                                </option>
                                                            ),
                                                        )}
                                                </Select>
                                            </Label>
                                            <ErrorMessage
                                                error={
                                                    form.formState.errors
                                                        .ethnicity
                                                }
                                            />
                                        </InputWrapper>
                                    </>
                                );
                            }}
                        />

                        <Controller
                            name="address_line_1"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Address line 1
                                            <Input
                                                {...field}
                                                hasError={
                                                    !!form.formState.errors
                                                        .address_line_1
                                                }
                                                data-cy="add-dentally-patient-address-line-1"
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors
                                                    .address_line_1
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            name="address_line_2"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Address line 2
                                            <Input
                                                {...field}
                                                hasError={
                                                    !!form.formState.errors
                                                        .address_line_2
                                                }
                                                data-cy="add-dentally-patient-address-line-2"
                                                placeholder="(Optional)"
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors
                                                    .address_line_2
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            name="postcode"
                            control={form.control}
                            render={({ field }) => {
                                return (
                                    <InputWrapper>
                                        <Label htmlFor={field.name}>
                                            Postcode
                                            <Input
                                                {...field}
                                                hasError={
                                                    !!form.formState.errors
                                                        .postcode
                                                }
                                                data-cy="add-dentally-patient-postcode"
                                            />
                                        </Label>
                                        <ErrorMessage
                                            error={
                                                form.formState.errors.postcode
                                            }
                                        />
                                    </InputWrapper>
                                );
                            }}
                        />

                        <Controller
                            control={form.control}
                            name="payment_plan_id"
                            render={({ field }) => {
                                const id = useId();
                                return (
                                    <>
                                        <InputWrapper>
                                            <Label htmlFor={id}>
                                                Payment plan
                                                <Select
                                                    id={id}
                                                    {...field}
                                                    data-cy="add-dentally-patient-payment-plan"
                                                >
                                                    <option value="">
                                                        Select
                                                    </option>
                                                    {paymentPlans.isFetched &&
                                                        paymentPlans.data &&
                                                        paymentPlans.data.map(
                                                            (plan) => (
                                                                <option
                                                                    key={
                                                                        plan.id
                                                                    }
                                                                    value={
                                                                        plan.id
                                                                    }
                                                                >
                                                                    {plan.patient_friendly_name ??
                                                                        plan.name}
                                                                </option>
                                                            ),
                                                        )}
                                                </Select>
                                            </Label>
                                            <ErrorMessage
                                                error={
                                                    form.formState.errors
                                                        .payment_plan_id
                                                }
                                            />
                                        </InputWrapper>
                                    </>
                                );
                            }}
                        />
                    </Form>
                </FormProvider>
            </Content>
            <Footer>
                <BackButton onClick={onBack}>
                    <Icon icon="ArrowLeft" color="gray-500" opacity={1} /> Back
                </BackButton>
                <NextButtons>
                    <Button
                        disabled={!proceedable}
                        onClick={onNext}
                        data-cy="flow-step-next-skip"
                    >
                        Skip
                    </Button>
                    <AddButton
                        disabled={!proceedable}
                        onClick={form.handleSubmit(onAddPatient)}
                        data-cy="flow-step-next"
                    >
                        Create patient
                    </AddButton>
                </NextButtons>
            </Footer>
        </Container>
    );
};

type ErrorMessageProps = {
    error?: FieldError;
};

const ErrorMessage = ({ error }: ErrorMessageProps) => {
    return (
        <div
            css={`
                display: none;
                ${mix.type({ level: 'small' })};
                color: var(--error-600);
                align-items: center;
                ${mix.gap({ size: 0.5 })}
                ${!!error &&
                css`
                    display: flex;
                `}
            `}
        >
            <Icon icon="AlertCircle" color="error-600" opacity={1} />
            <div>{error?.message}</div>
        </div>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    ${mix.height({ size: 9 })};
    min-height: ${mix.unit({ size: 9 })};
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: 'Inter', sans-serif;
    font-size: ${mix.unit({ size: 2.25 })};
    line-height: ${mix.unit({ size: 3 })};
    font-weight: 600;
    color: var(--gray-600);
    ${mix.padding({ padding: [0, 4] })};
    border-bottom: 1px solid var(--gray-200);
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    ${mix.gap({ size: 2 })};
    ${mix.padding({ padding: [4] })};
    border-bottom: 1px solid var(--gray-200);
    max-height: ${mix.unit({ size: 75 })};
    overflow-y: scroll;
`;

const Footer = styled.div`
    ${mix.height({ size: 10.5 })};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    ${mix.padding({ padding: [4] })};
`;

const NextButtons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    ${mix.gap({ size: 0.5 })};
`;

const Button = styled.button`
    ${mix.padding({ padding: [1, 0.2, 1, 0.2] })};
    ${mix.height({ size: 4.5 })};
    ${mix.width({ size: 6 })};

    outline: none;
    border: none;
    border-radius: ${mix.unit({ size: 0.5 })};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: 300ms ease;
    pointer-events: auto;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: var(--gray-500);
    gap: ${mix.unit({ size: 1 })};
    background-color: white;
    font-family: 'Roboto', sans-serif;

    &:disabled {
        color: var(--gray-300);
        background-color: var(--gray-100);
    }

    &:hover:not([disabled]) {
        background-color: var(--gray-100);
    }
`;

const BackButton = styled(Button)`
    border: 1px solid;
    border-color: var(--gray-200);
    ${mix.width({ size: 11 })};
`;

const AddButton = styled(Button)`
    ${mix.padding({ padding: [1] })};
    border: 1px solid var(--primary-100);
    background-color: white;
    color: var(--primary-500);
    ${mix.width({ size: 15 })};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    ${mix.gap({ size: 2 })};
`;

const Label = styled.label`
    color: var(--gray-600);
    font-weight: 600;
    display: block;
`;

const NameHolder = styled.div`
    display: flex;
    flex-direction: row;
    ${mix.gap({ size: 1.5 })}
`;

const Select = styled.select`
    ${mix.type({ level: 'body2' })};
    ${mix.padding({ padding: 1.5 })};
    ${mix.height({ size: 6 })};
    border-radius: 4px;
    border: 1px solid var(--gray-300);
    width: 100%;
    font-weight: 400;
`;

const InputWrapper = styled.div`
    ${mix.gap({ size: 1 })}
    display: flex;
    flex-direction: column;
`;

const Input = styled.input<{ hasError: boolean }>`
    ${mix.padding({ padding: 1.5 })};
    border: 1px solid
        ${(props) => (props.hasError ? 'var(--error-300)' : 'var(--gray-300)')};
    border-radius: 4px;
    width: 100%;
    font-weight: 400;

    ::placeholder {
        color: var(--gray-300);
    }
`;

const InputWithMask = styled(InputMask)`
    ${mix.padding({ padding: 1.5 })};
    border: 1px solid var(--gray-300);
    border-radius: 4px;
    width: 100%;
    font-weight: 400;
`;

const ToggleHolder = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0;
    width: 100%;
`;

const ToggleButton = styled.div<{ $isLeft: boolean; $isSelected: boolean }>`
    height: ${mix.unit({ size: 5.5 })};
    ${mix.padding({ padding: 1.5 })};
    gap: 12px;
    border: 1px solid var(--gray-300);
    border-radius: 0;
    width: 100%;
    text-align: center;
    cursor: pointer;

    ${({ $isLeft }) =>
        $isLeft &&
        css`
            border-top-left-radius: ${mix.unit({ size: 0.5 })};
            border-bottom-left-radius: ${mix.unit({ size: 0.5 })};
            border-right: 0;
        `}

    ${({ $isLeft }) =>
        !$isLeft &&
        css`
            border-top-right-radius: ${mix.unit({ size: 0.5 })};
            border-bottom-right-radius: ${mix.unit({ size: 0.5 })};
        `}

    ${({ $isSelected }) =>
        $isSelected &&
        css`
            background-color: var(--primary-100);
        `};
`;
